import type {
  GateCriterion,
  StageGateResponse,
} from "@patent-studio/api-types";
import {
  subStepPath,
  tabPath,
  type GuideId,
  type V2StageId,
} from "@/v2/stage-vocabulary";

/** The stages the stage gate holds shut until the Thesis has one yes of each kind. */
export type GatedStageId = Extract<
  V2StageId,
  "claim-strategy" | "planning-outline" | "generate"
>;

/**
 * What the locked page calls each gated stage, the walkthrough it offers, and
 * whether it carries the drop banner, as v0.28 draws them: Generate has none.
 */
export const GATED_STAGES: Readonly<
  Record<GatedStageId, { name: string; guide: GuideId; dropBanner: boolean }>
> = {
  "claim-strategy": {
    name: "Claim Strategy",
    guide: "claim-strategy",
    dropBanner: true,
  },
  "planning-outline": {
    name: "Specification",
    guide: "specification",
    dropBanner: true,
  },
  generate: {
    name: "Generate Patent Draft",
    guide: "generate",
    dropBanner: false,
  },
};

/**
 * Which stages the rail marks shut: every gated one while the served gate says
 * shut, and none while it is unread, so no lock is drawn on a guess.
 */
export function shutStages(
  gate: StageGateResponse | undefined,
): Record<GatedStageId, boolean> {
  const shut = gate !== undefined && !gate.open;
  return { "claim-strategy": shut, "planning-outline": shut, generate: shut };
}

/**
 * Each criterion as the locked page lists it: v0.28's words, where it is met,
 * and the way there. Whether it is met, and how many meet it, is the server's.
 */
export const CRITERION_ROWS: Readonly<
  Record<
    GateCriterion,
    {
      label: string;
      /** What the drop banner says the Thesis no longer has a confirmed one of. */
      noun: string;
      where: string;
      tab: string;
      to: (taskId: string) => string;
    }
  >
> = {
  element_confirmed: {
    label: "An element confirmed",
    noun: "element",
    where: "Thesis / Invention",
    tab: "Invention",
    to: (taskId) => subStepPath(taskId, "thesis", "invention"),
  },
  position_confirmed: {
    label: "A position confirmed",
    noun: "position",
    where: "Define Positions",
    tab: "Define Positions",
    to: (taskId) => tabPath(taskId, "thesis", "novelty", "positions"),
  },
  assessment_accepted: {
    label: "A novelty assessment accepted",
    noun: "novelty assessment",
    where: "Novelty Analysis",
    tab: "Novelty Analysis",
    to: (taskId) => tabPath(taskId, "thesis", "novelty", "analysis"),
  },
};
